import {
  getNumberPackageList,
  type INumberPackageDto,
} from '@dbl/services/numberPackage';
import { useRequest } from 'ahooks';
import React, {useEffect, useMemo, useState} from 'react';
import {
  Button,
  Select,
  Form,
  Modal,
  SelectOption,
  StatusTip,
  Input
} from 'tea-component';
import {checkNullString, useDblIntl} from '@/pages/datalab/utils/utils';
import type { IGetTableInfoDto } from '@dbl/services/dataSource';
export default function AssociationModel(props: any) {
  const {
    gameCode,
    userPackages,
    visible,
    getSuccess,
    close,
    dataTables,
  }: {
    gameCode: string;
    userPackages: INumberPackageDto[];
    visible: boolean;
    getSuccess: Function;
    close: Function;
    dataTables: IGetTableInfoDto[];
  } = props;
  const { dblFormatMessage, commonFormatMessage } = useDblIntl("multi");
  const [selectPacket, setSelectPacket] = useState<INumberPackageDto | undefined>();
  const [page, setPage] = useState(1);
  const [showSearchKeyWord, setShowSearchKeyWord] = useState<string>('');
  const [searchKeyWord, setSearchKeyWord] = useState<string>('');
  const [status, setStatus] = useState<'' | 'loading' | 'empty' | 'error'>('');
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [selectTableFields, setSelectTableFields] = useState<any>({});

  const { run } = useRequest(
    (page, keywords = searchKeyWord) => {
      const params = {
        game_code: gameCode,
        keywords,
        page,
        page_size: 10,
      };
      setStatus('loading');
      return getNumberPackageList(params);
    },
    {
      manual: true,
      onSuccess: (res: any) => {
        setTotal(res?.row_count || 0);

        let newOptions = (res?.data_list || []).map((item: any) => ({
          value: `${item.package_uuid}`,
          text: (
            <div className="select">
              <div className="select-title">{item.name}</div>
              <div className="select-flex">
                <div className="select-num">{item.package_size}</div>
                <div className="select-text">{item.remark}</div>
              </div>
            </div>
          ),
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          numberPackage: {
            ...item,
          } as INumberPackageDto,
        }));
        if (page > 1) {
          newOptions = [...options, ...newOptions];
        }

        setOptions(newOptions);
        if (newOptions.length === 0) {
          setStatus('empty');
        } else {
          setStatus('');
        }
      },
      onError: () => {
        setStatus('error');
      },
    },
  );

  useEffect(() => {
    if (visible) {
      setSelectPacket(userPackages[0]);
      setPage(1);
      run(1);
    }
  }, [visible]);
  
  // 生成关联数据表的敏感字段Select数据
  const tableSelectFields = useMemo(() => {
    if (!Array.isArray(dataTables)) return [];
    const getOptions = (source) => {
      if (!Array.isArray(source)) return [];
      const options: { value: any; text: any; }[] = [];
      source.forEach((data) => {
        if (data.is_sensitive)
          options.push({value: data.name, text: data.display_name});
      });
      return options;
    };
    
    const fields: any[] = [];
    dataTables.forEach(table => {
      const options = getOptions(table?.column_list);
      if (options.length > 0)
        fields.push({
          options,
          tableCode: table?.table_code,
          tableId: table?.table_id,
          tableName: table?.table_name,
          onlyOneTable: table?.only_one_table
        });
    });
    const relate_column = userPackages?.[0]?.relate_column || [];
    setSelectTableFields(fields.reduce((acc, field) => {
      const temp = relate_column.find(col => col.table_code === field.tableCode && col.table_id === field.tableId);
      acc[field?.tableCode] = {
        table_code: field?.tableCode,
        table_id: field?.tableId,
        column: temp ? temp.column : field?.options?.[0]?.value
      };
      return acc;
    }, {}));
    return fields;
  }, [dataTables]);
  return (
    <Modal
      className="model-add"
      visible={visible}
      caption={dblFormatMessage("related_number_package")}
      onClose={() => {
        close();
      }}
    >
      <Modal.Body>
        <Form layout={"fixed"}>
          <Form.Item label={dblFormatMessage("number_package_name")}>
            <Select
              className="model-add-search"
              searchable={!checkNullString(searchKeyWord) || options.length > 0}
              clearable
              matchButtonWidth
              size="m"
              appearance="button"
              placeholder={dblFormatMessage("number_package_name_placeholder")}
              searchPlaceholder={dblFormatMessage("number_package_name_search")}
              button={selectPacket ? selectPacket?.name : undefined}
              options={options}
              value={selectPacket?.package_uuid ? `${selectPacket.package_uuid}` : ''}
              onChange={(_value, context) => {
                setSelectPacket(context.option?.numberPackage);
              }}
              filter={() => true}
              searchValue={showSearchKeyWord}
              onSearch={(keyword) => {
                if (keyword === searchKeyWord) {
                  return;
                }
                setShowSearchKeyWord(keyword ? keyword : '');
                setOptions([]);
                setSearchKeyWord(keyword ? keyword : '');
                setPage(1);
                run(1, keyword);
              }}
              onOpenChange={(open: boolean) => {
                if (open) {
                  setShowSearchKeyWord(searchKeyWord);
                }
              }}
              onScrollBottom={() => {
                if (status === '' && options.length < total) {
                  const newPage = page + 1;
                  setPage(newPage);
                  run(newPage);
                }
              }}
              tips={
                status &&
                options.length === 0 && (
                  <StatusTip
                    status={status}
                    emptyText={commonFormatMessage("no_data")}
                    loadingText={commonFormatMessage("loading")}
                    errorText={commonFormatMessage("load_fail")}
                    retryText={commonFormatMessage("refresh")}
                    onRetry={() => {
                      setPage(1);
                      run(1);
                    }}
                  />
                )
              }
              bottomTips={
                status &&
                options.length > 0 && (
                  <StatusTip
                    status={status}
                    onRetry={() => {
                      run(page);
                    }}
                    loadingText={commonFormatMessage("loading")}
                    errorText={commonFormatMessage("load_fail")}
                    retryText={commonFormatMessage("refresh")}
                  />
                )
              }
            />
          </Form.Item>
          {
            (!selectPacket?.package_uuid) && (<Form.Item>
              <Form.Text className="model-add-text">
                {dblFormatMessage("number_package_name_info")}
              </Form.Text>
            </Form.Item>)
          }
          {
            selectPacket?.package_uuid && (<>
              <Form.Item label={dblFormatMessage("number_package_sensitive_fields")}>
                <Input value={JSON.parse(selectPacket?.column_infos || "{}").column} disabled className="model-add-search"/>
              </Form.Item>
              {
                tableSelectFields.map(field => {
                  const {options, tableCode, tableId, tableName, onlyOneTable } = field;
                  return (
                    <Form.Item
                      key={`${tableCode}.${tableId}`}
                      label={`${onlyOneTable ? '' : `${tableCode}.`}${tableName}`}
                    >
                      <Select
                        className="model-add-search"
                        appearance="button"
                        options={options}
                        value={selectTableFields[tableCode]?.column}
                        onChange={value => {
                          setSelectTableFields({...selectTableFields, [tableCode]: {table_code: tableCode, table_id: tableId, column: value}});
                        }}
                      />
      
                    </Form.Item>
                  );
                })
              }
            </>)
          }
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          onClick={() => {
            close();
            getSuccess(selectPacket, selectTableFields);
          }}
        >
          {commonFormatMessage("confirm")}
        </Button>
        <Button
          type="weak"
          onClick={() => {
            close();
          }}
        >
          {commonFormatMessage("cancel")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
